import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, useCallback } from 'react'
import { cn } from '../../lib/utils'

const pressableVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-inverse focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 duration-0',
  {
    variants: {
      variant: {
        core: 'bg-dark-bg text-header cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'core',
    },
  },
)

export interface PressableProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof pressableVariants> {
  onPress?: () => void
  disabled?: boolean
  tabIndex?: number
}

const Pressable = forwardRef<HTMLDivElement, PressableProps>(
  ({ onPress, disabled = false, tabIndex = 0, className = '', variant, ...props }: PressableProps, ref) => {
    const isPressable = typeof onPress === 'function' && !disabled

    const handlePress = useCallback(() => {
      if (!isPressable) return
      onPress?.()
    }, [isPressable, onPress])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && isPressable) {
          e.preventDefault()
          handlePress()
        }
      },
      [handlePress, isPressable],
    )

    return (
      <div
        role={isPressable ? 'button' : undefined}
        tabIndex={isPressable ? tabIndex : undefined}
        className={cn(pressableVariants({ variant, className }))}
        onClick={handlePress}
        onKeyDown={handleKeyDown}
        ref={ref}
        {...props}
      />
    )
  },
)
Pressable.displayName = 'Pressable'

export { Pressable, pressableVariants }
